import type { EditorialChecklistProgress } from "@/lib/editorial-checklist-progress";
import { countEditorialChecklistProgress } from "@/lib/editorial-checklist-progress";
import {
  areEventDatesInOrder,
  eventCalendarDayToEndIso,
  eventCalendarDayToStartIso,
  eventIsoToCalendarDay,
  isEventCalendarDay,
  parisTodayCalendarDay,
} from "@/lib/event-dates";
import type {
  ArticleAssigneeOption,
  ArticleEditStatusValue,
  CreateContentInitialStatus,
} from "@/core/services/types/article.types";
import {
  ARTICLE_EDIT_STATUS_VALUES,
  editStatusToDbFields,
  parseDatetimeLocalValue,
  resolveArticleEditStatus,
  toDatetimeLocalValue,
} from "@/core/services/types/article.types";
import {
  appendLibrarySortParams,
  getNextLibrarySort,
  parseLibrarySortDirection,
  type LibrarySortDirection,
} from "@/core/services/types/library-sort.types";
import {
  DEFAULT_LIBRARY_PAGE_SIZE,
  appendLibraryPageSizeParam,
  parseLibraryPageSize,
  type LibraryPageSize,
} from "@/core/services/types/library-pagination.types";
import type { Enums, Tables } from "@/types/database.types";

export const EVENT_CATEGORY_VALUES = [
  "PERMANENT_COLLECTION",
  "TEMPORARY_COLLECTION",
  "ACTIVITY",
] as const satisfies readonly Enums<"event_category_enum">[];

export type EventCategoryValue = (typeof EVENT_CATEGORY_VALUES)[number];

export const ACTIVITY_TYPE_VALUES = [
  "SPECTACLE_EVENT",
  "THEMATIC_VISIT",
  "WORKSHOP",
] as const satisfies readonly Enums<"activity_type_enum">[];

export type ActivityTypeValue = (typeof ACTIVITY_TYPE_VALUES)[number];

export const ACTIVITY_RECURRENCE_VALUES = [
  "Chaque jour",
  "Chaque semaine",
  "Chaque quinzaine",
  "Chaque mois",
] as const;

export type ActivityRecurrenceValue = (typeof ACTIVITY_RECURRENCE_VALUES)[number];

export const ACTIVITY_WEEKDAY_VALUES = [0, 1, 2, 3, 4, 5, 6] as const;

export type ActivityWeekdayValue = (typeof ACTIVITY_WEEKDAY_VALUES)[number];

export type ActivitySchedulePonctuel = {
  dates: string[];
};

export type ActivityScheduleRecurrent = {
  reccurence: ActivityRecurrenceValue;
  days: number[];
};

export type ActivitySchedule =
  | ActivitySchedulePonctuel
  | ActivityScheduleRecurrent;

export function emptyPonctuelSchedule(): ActivitySchedulePonctuel {
  return { dates: [] };
}

export function emptyRecurrentSchedule(): ActivityScheduleRecurrent {
  return { reccurence: "Chaque semaine", days: [] };
}

export function todayISO(): string {
  return parisTodayCalendarDay();
}

export function deriveActivityBounds(
  isRecurrent: boolean,
  schedule: ActivitySchedule,
): { start_date: string; end_date: string } | null {
  if (isRecurrent) {
    const today = todayISO();
    const start = eventCalendarDayToStartIso(today);
    const end = eventCalendarDayToEndIso("2099-12-31");
    if (!start || !end) return null;
    return { start_date: start, end_date: end };
  }

  const dates = [...(schedule as ActivitySchedulePonctuel).dates].sort();
  if (dates.length === 0) return null;

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  if (!firstDate || !lastDate) return null;

  const start = eventCalendarDayToStartIso(firstDate);
  const end = eventCalendarDayToEndIso(lastDate);
  if (!start || !end) return null;

  return { start_date: start, end_date: end };
}

export function normalizePonctuelSchedule(
  dates: string[],
): ActivitySchedulePonctuel {
  const unique = [
    ...new Set(
      dates.filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date)),
    ),
  ].sort();

  return { dates: unique };
}

export function normalizeRecurrentSchedule(
  reccurence: ActivityRecurrenceValue,
  days: number[],
): ActivityScheduleRecurrent {
  const uniqueDays = [
    ...new Set(days.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)),
  ].sort((a, b) => a - b);

  return { reccurence, days: uniqueDays };
}

export function parseActivityScheduleFromDb(
  raw: unknown,
  isRecurrent: boolean,
): ActivitySchedule {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return isRecurrent ? emptyRecurrentSchedule() : emptyPonctuelSchedule();
  }

  const record = raw as Record<string, unknown>;

  if (isRecurrent) {
    const reccurence =
      typeof record.reccurence === "string" &&
      ACTIVITY_RECURRENCE_VALUES.includes(
        record.reccurence as ActivityRecurrenceValue,
      )
        ? (record.reccurence as ActivityRecurrenceValue)
        : "Chaque semaine";

    const days = Array.isArray(record.days)
      ? record.days.filter(
          (day): day is number =>
            typeof day === "number" &&
            Number.isInteger(day) &&
            day >= 0 &&
            day <= 6,
        )
      : [];

    return normalizeRecurrentSchedule(reccurence, days);
  }

  const dates = Array.isArray(record.dates)
    ? record.dates.filter(
        (date): date is string =>
          typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date),
      )
    : [];

  return normalizePonctuelSchedule(dates);
}

export type ActivityPublishValidationMessages = {
  titleRequired: string;
  placeRequired: string;
  activityTypeRequired: string;
  activityDatesRequired: string;
  activityRecurrenceRequired: string;
  activityDaysRequired: string;
  startDateRequired: string;
  endDateRequired: string;
  endDateBeforeStart: string;
};

export {
  ARTICLE_EDIT_STATUS_VALUES as EVENT_EDIT_STATUS_VALUES,
  editStatusToDbFields,
  parseDatetimeLocalValue,
  toDatetimeLocalValue,
};

/** @deprecated Use eventIsoToCalendarDay from @/lib/event-dates */
export function toDateInputValue(iso: string | null): string {
  return eventIsoToCalendarDay(iso);
}

/** @deprecated Use eventCalendarDayToStartIso from @/lib/event-dates */
export function parseDateInputValue(value: string): string | null {
  return eventCalendarDayToStartIso(value);
}

/** @deprecated Use eventCalendarDayToEndIso from @/lib/event-dates */
export function toEndOfDayIso(dateInput: string): string {
  return eventCalendarDayToEndIso(dateInput) ?? "";
}

export type EventLibraryDateFilters = {
  startFrom: string | null;
  startTo: string | null;
  endFrom: string | null;
  endTo: string | null;
};

export const EMPTY_EVENT_LIBRARY_DATE_FILTERS: EventLibraryDateFilters = {
  startFrom: null,
  startTo: null,
  endFrom: null,
  endTo: null,
};

export function getEventLibrarySliderDateBounds(): { min: string; max: string } {
  const year = new Date().getFullYear();
  return {
    min: `${year - 1}-01-01`,
    max: `${year + 2}-12-31`,
  };
}

export function daysBetweenDateInputs(start: string, end: string): number {
  const startMs = new Date(`${start}T00:00:00`).getTime();
  const endMs = new Date(`${end}T00:00:00`).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 0;
  return Math.max(0, Math.round((endMs - startMs) / 86_400_000));
}

export function addDaysToDateInput(date: string, days: number): string {
  const next = new Date(`${date}T00:00:00`);
  next.setDate(next.getDate() + days);
  const year = next.getFullYear();
  const month = String(next.getMonth() + 1).padStart(2, "0");
  const day = String(next.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function fromDateToSliderIndex(
  from: string | null,
  minDate: string,
): number {
  if (!from) return 0;
  return daysBetweenDateInputs(minDate, from);
}

export function toDateToSliderIndex(
  to: string | null,
  minDate: string,
  maxDate: string,
): number {
  if (!to) return daysBetweenDateInputs(minDate, maxDate);
  return daysBetweenDateInputs(minDate, to);
}

export function sliderIndexToFromDate(
  index: number,
  minDate: string,
): string | null {
  if (index <= 0) return null;
  return addDaysToDateInput(minDate, index);
}

export function sliderIndexToToDate(
  index: number,
  minDate: string,
  maxDate: string,
): string | null {
  const maxIndex = daysBetweenDateInputs(minDate, maxDate);
  if (index >= maxIndex) return null;
  return addDaysToDateInput(minDate, index);
}

export function parseEventLibraryDateParam(
  value: string | undefined,
): string | null {
  if (!value) return null;
  return isEventCalendarDay(value) ? value : null;
}

export function parseEventLibraryDateFilters(searchParams: {
  get: (key: string) => string | null;
}): EventLibraryDateFilters {
  const legacyStartOn = parseEventLibraryDateParam(
    searchParams.get("start_on") ?? undefined,
  );
  const legacyEndOn = parseEventLibraryDateParam(
    searchParams.get("end_on") ?? undefined,
  );

  return {
    startFrom:
      parseEventLibraryDateParam(searchParams.get("start_from") ?? undefined) ??
      legacyStartOn,
    startTo:
      parseEventLibraryDateParam(searchParams.get("start_to") ?? undefined) ??
      legacyStartOn,
    endFrom:
      parseEventLibraryDateParam(searchParams.get("end_from") ?? undefined) ??
      legacyEndOn,
    endTo:
      parseEventLibraryDateParam(searchParams.get("end_to") ?? undefined) ??
      legacyEndOn,
  };
}

export function countActiveEventLibraryFilters(
  category: EventLibraryCategoryFilter,
  dateFilters: EventLibraryDateFilters,
): number {
  let count = category !== "ALL" ? 1 : 0;

  if (dateFilters.startFrom || dateFilters.startTo) {
    count += 1;
  }

  if (dateFilters.endFrom || dateFilters.endTo) {
    count += 1;
  }

  return count;
}
export type EventEditStatusValue = ArticleEditStatusValue;
export type EventAssigneeOption = ArticleAssigneeOption;

export const EVENT_LIBRARY_TABS = [
  "ALL",
  "DRAFT",
  "PENDING_REVIEW",
  "PUBLISHED",
  "ARCHIVED",
] as const;

export type EventLibraryTab = (typeof EVENT_LIBRARY_TABS)[number];

export type EventAdminListAssignee = Pick<
  Tables<"team_members">,
  "id" | "first_name" | "last_name"
>;

export type EventAdminListPlace = Pick<Tables<"places">, "name" | "city">;

export type EventAdminListItem = Pick<
  Tables<"events">,
  | "id"
  | "title"
  | "published_at"
  | "created_at"
  | "start_date"
  | "end_date"
  | "updated_at"
  | "status"
  | "assigned_to"
  | "event_category"
  | "cover_image_url"
  | "place_id"
  | "description_short"
> & {
  assignee: EventAdminListAssignee | null;
  place: EventAdminListPlace | null;
};

export type EventAdminExportPlace = Pick<
  Tables<"places">,
  "name" | "city" | "zip_code" | "department_name"
>;

export type EventAdminExportItem = Pick<
  Tables<"events">,
  "id" | "title" | "description_short" | "start_date" | "end_date" | "department_name"
> & {
  place: EventAdminExportPlace | null;
};

export type EventListPage = {
  items: EventAdminListItem[];
  total: number;
  page: number;
  pageSize: LibraryPageSize;
};

export type EventLibraryTabCounts = Record<EventLibraryTab, number>;

export const EVENT_LIBRARY_CATEGORY_FILTERS = [
  "ALL",
  "PERMANENT_COLLECTION",
  "TEMPORARY_COLLECTION",
  "ACTIVITY",
] as const;

export type EventLibraryCategoryFilter =
  (typeof EVENT_LIBRARY_CATEGORY_FILTERS)[number];

export type EventLibraryCategoryCounts = Record<
  EventLibraryCategoryFilter,
  number
>;

export type EventLibraryKpis = {
  total: number;
  publishedThisMonth: number;
  pendingReview: number;
};

export const EVENT_LIBRARY_PAGE_SIZE = DEFAULT_LIBRARY_PAGE_SIZE;

export { parseLibraryPageSize as parseEventPageSize };
export const EVENT_LIBRARY_EXPORT_MAX_ROWS = 10_000;
export const EVENT_SEARCH_MAX_LENGTH = 100;

export function getEventSharePath(id: string): string {
  return `/events/redaction/${id}`;
}

export function parseEventLibraryTab(value: string | undefined): EventLibraryTab {
  if (value && EVENT_LIBRARY_TABS.includes(value as EventLibraryTab)) {
    return value as EventLibraryTab;
  }
  return "ALL";
}

export function parseEventLibraryCategory(
  value: string | undefined,
): EventLibraryCategoryFilter {
  if (
    value &&
    EVENT_LIBRARY_CATEGORY_FILTERS.includes(value as EventLibraryCategoryFilter)
  ) {
    return value as EventLibraryCategoryFilter;
  }
  return "ALL";
}

export function buildEventLibraryHref({
  tab,
  page = 1,
  pageSize = DEFAULT_LIBRARY_PAGE_SIZE,
  searchQuery = "",
  category = "ALL",
  dateFilters = EMPTY_EVENT_LIBRARY_DATE_FILTERS,
  sort = null,
}: {
  tab: EventLibraryTab;
  page?: number;
  pageSize?: LibraryPageSize;
  searchQuery?: string;
  category?: EventLibraryCategoryFilter;
  dateFilters?: EventLibraryDateFilters;
  sort?: EventLibrarySortState;
}): string {
  const params = new URLSearchParams({
    status: tab,
    page: String(page),
  });

  if (searchQuery) {
    params.set("q", searchQuery);
  }

  if (category !== "ALL") {
    params.set("category", category);
  }

  if (dateFilters.startFrom) {
    params.set("start_from", dateFilters.startFrom);
  }

  if (dateFilters.startTo) {
    params.set("start_to", dateFilters.startTo);
  }

  if (dateFilters.endFrom) {
    params.set("end_from", dateFilters.endFrom);
  }

  if (dateFilters.endTo) {
    params.set("end_to", dateFilters.endTo);
  }

  appendLibrarySortParams(params, sort);
  appendLibraryPageSizeParam(params, pageSize);

  return `/events/library?${params.toString()}`;
}

export function buildEventLibrarySortHref({
  tab,
  searchQuery = "",
  pageSize = DEFAULT_LIBRARY_PAGE_SIZE,
  category = "ALL",
  dateFilters = EMPTY_EVENT_LIBRARY_DATE_FILTERS,
  sort,
  nextColumn,
}: {
  tab: EventLibraryTab;
  searchQuery?: string;
  pageSize?: LibraryPageSize;
  category?: EventLibraryCategoryFilter;
  dateFilters?: EventLibraryDateFilters;
  sort: EventLibrarySortState;
  nextColumn: EventLibrarySortColumn;
}): string {
  return buildEventLibraryHref({
    tab,
    page: 1,
    pageSize,
    searchQuery,
    category,
    dateFilters,
    sort: getNextLibrarySort(sort, nextColumn),
  });
}

export function parseEventPage(value: string | undefined): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return 1;
  return parsed;
}

export function parseEventSearchQuery(value: string | undefined): string {
  if (!value) return "";
  return value.trim().slice(0, EVENT_SEARCH_MAX_LENGTH);
}

export const EVENT_LIBRARY_SORT_COLUMNS = [
  "title",
  "event_category",
  "place",
  "assignment",
  "start_date",
  "end_date",
  "created_at",
  "updated_at",
  "published_at",
  "status",
] as const;

export type EventLibrarySortColumn = (typeof EVENT_LIBRARY_SORT_COLUMNS)[number];

export type EventLibrarySort = {
  column: EventLibrarySortColumn;
  direction: LibrarySortDirection;
};

export type EventLibrarySortState = EventLibrarySort | null;

export function parseEventLibrarySort(
  column: string | undefined,
  direction: string | undefined,
): EventLibrarySortState {
  if (
    !column ||
    !EVENT_LIBRARY_SORT_COLUMNS.includes(column as EventLibrarySortColumn)
  ) {
    return null;
  }

  return {
    column: column as EventLibrarySortColumn,
    direction: parseLibrarySortDirection(direction),
  };
}

export type EventSummary = Pick<
  Tables<"events">,
  | "id"
  | "title"
  | "description_short"
  | "cover_image_url"
  | "start_date"
  | "end_date"
  | "published_at"
  | "event_category"
>;

export type Event = EventSummary &
  Pick<
    Tables<"events">,
    | "description_long"
    | "pricing_info"
    | "place_id"
    | "whart_review_title"
    | "whart_review_content"
    | "whart_else"
  >;

export type EventEditor = Pick<
  Tables<"events">,
  | "id"
  | "title"
  | "description_short"
  | "description_long"
  | "cover_image_url"
  | "status"
  | "published_at"
  | "assigned_to"
  | "updated_at"
  | "start_date"
  | "end_date"
  | "place_id"
  | "event_category"
  | "pricing_info"
  | "whart_review_title"
  | "whart_review_content"
  | "whart_else"
  | "activity_type"
  | "is_family"
  | "is_recurrent"
  | "activity_schedule"
  | "internal_notes"
>;

export type UpdateEventContentInput = Pick<
  EventEditor,
  | "title"
  | "description_short"
  | "description_long"
  | "event_category"
  | "whart_review_title"
  | "whart_review_content"
  | "whart_else"
  | "pricing_info"
  | "activity_type"
  | "is_family"
  | "is_recurrent"
  | "activity_schedule"
> & {
  start_date?: EventEditor["start_date"];
  end_date?: EventEditor["end_date"];
};

export function getEventPublishFieldErrors(
  event: Pick<
    EventEditor,
    | "title"
    | "place_id"
    | "event_category"
    | "start_date"
    | "end_date"
    | "activity_type"
    | "is_recurrent"
    | "activity_schedule"
  >,
  messages: ActivityPublishValidationMessages,
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  if (!event.title.trim()) {
    fieldErrors.title = messages.titleRequired;
  }

  if (!event.place_id) {
    fieldErrors.place_id = messages.placeRequired;
  }

  if (event.event_category === "ACTIVITY") {
    if (!event.activity_type) {
      fieldErrors.activity_type = messages.activityTypeRequired;
    }

    const schedule = parseActivityScheduleFromDb(
      event.activity_schedule,
      event.is_recurrent,
    );

    if (event.is_recurrent) {
      const recurrent = schedule as ActivityScheduleRecurrent;
      if (!recurrent.reccurence) {
        fieldErrors.activity_schedule = messages.activityRecurrenceRequired;
      }
      if (recurrent.days.length === 0) {
        fieldErrors.activity_days = messages.activityDaysRequired;
      }
    } else {
      const ponctuel = schedule as ActivitySchedulePonctuel;
      if (ponctuel.dates.length === 0) {
        fieldErrors.activity_schedule = messages.activityDatesRequired;
      }
    }

    if (!event.start_date) {
      fieldErrors.start_date = messages.startDateRequired;
    }

    if (!event.end_date) {
      fieldErrors.end_date = messages.endDateRequired;
    }

    if (
      event.start_date &&
      event.end_date &&
      !areEventDatesInOrder(event.start_date, event.end_date)
    ) {
      fieldErrors.end_date = messages.endDateBeforeStart;
    }

    return fieldErrors;
  }

  if (event.event_category === "PERMANENT_COLLECTION") {
    return fieldErrors;
  }

  if (!event.start_date) {
    fieldErrors.start_date = messages.startDateRequired;
  }

  if (!event.end_date) {
    fieldErrors.end_date = messages.endDateRequired;
  }

  if (
    event.start_date &&
    event.end_date &&
    !areEventDatesInOrder(event.start_date, event.end_date)
  ) {
    fieldErrors.end_date = messages.endDateBeforeStart;
  }

  return fieldErrors;
}

export function getEventContentPublishFieldErrors(
  event: Parameters<typeof getEventPublishFieldErrors>[0],
  messages: ActivityPublishValidationMessages,
): Record<string, string> {
  const fieldErrors = getEventPublishFieldErrors(event, messages);
  delete fieldErrors.place_id;
  return fieldErrors;
}

export type UpdateEventStatusInput = {
  editStatus: EventEditStatusValue;
};

export type UpdateEventAssignmentInput = {
  assignedTo: string | null;
};

export type UpdateEventInternalNotesInput = {
  internalNotes: string | null;
};

export type UpdateEventCoverImageInput = {
  coverImageUrl: string | null;
};

export type UpdateEventPlaceInput = {
  placeId: string;
};

export type EventEditorialChecklistState = {
  hasCoverImage: boolean;
  hasPlace: boolean;
  hasTitleAndDescription: boolean;
  requiresDates: boolean;
  hasDates: boolean;
};

function hasEventExhibitionDates(
  startDate: string | null,
  endDate: string | null,
): boolean {
  return areEventDatesInOrder(startDate, endDate);
}

export function getEventEditorialChecklist(
  event: Pick<
    EventEditor,
    | "cover_image_url"
    | "place_id"
    | "title"
    | "description_short"
    | "event_category"
    | "start_date"
    | "end_date"
  >,
): EventEditorialChecklistState {
  const title = event.title?.trim() ?? "";
  const hasTitle = Boolean(title) && title !== EVENT_CREATE_DEFAULT_TITLE;
  const hasDescription = Boolean(event.description_short?.trim());
  const requiresDates =
    event.event_category === "TEMPORARY_COLLECTION" ||
    event.event_category === "ACTIVITY";

  return {
    hasCoverImage: Boolean(event.cover_image_url?.trim()),
    hasPlace: Boolean(event.place_id),
    hasTitleAndDescription: hasTitle && hasDescription,
    requiresDates,
    hasDates:
      !requiresDates ||
      hasEventExhibitionDates(event.start_date, event.end_date),
  };
}

export function isEventEditorialChecklistComplete(
  event: Pick<
    EventEditor,
    | "cover_image_url"
    | "place_id"
    | "title"
    | "description_short"
    | "event_category"
    | "start_date"
    | "end_date"
  >,
): boolean {
  const checklist = getEventEditorialChecklist(event);
  return (
    checklist.hasCoverImage &&
    checklist.hasPlace &&
    checklist.hasTitleAndDescription &&
    checklist.hasDates
  );
}

export function getEventEditorialChecklistProgress(
  event: Pick<
    EventEditor,
    | "cover_image_url"
    | "place_id"
    | "title"
    | "description_short"
    | "event_category"
    | "start_date"
    | "end_date"
  >,
): EditorialChecklistProgress {
  const checklist = getEventEditorialChecklist(event);
  return countEditorialChecklistProgress([
    checklist.hasCoverImage,
    checklist.hasPlace,
    checklist.hasTitleAndDescription,
    ...(checklist.requiresDates ? [checklist.hasDates] : []),
  ]);
}

export type UpdateEventState =
  | { success: true; data: EventEditor }
  | { success: false; error?: string; fieldErrors?: Record<string, string> };

export type UploadEventCoverImageResult =
  | { success: true; data: EventEditor }
  | { success: false; error: string };

export const EVENT_CREATE_DEFAULT_TITLE = "Nouvel événement";

export type CreateEventInput = {
  title?: string;
  placeId?: string | null;
  startDate?: string;
  endDate?: string;
  status?: CreateContentInitialStatus;
  assignedTo?: string | null;
};

export type CreateEventResult =
  | { success: true; data: EventEditor }
  | { success: false; error: string };

export type EventTaxonomy = {
  artFormIds: string[];
  artisticPeriodIds: string[];
  culturalAreaIds: string[];
  exhibitionTypeIds: string[];
};

export type UpdateEventTaxonomyInput = EventTaxonomy;

export type UpdateEventTaxonomyResult =
  | { success: true }
  | { success: false; error: string };

export function resolveEventEditStatus(event: {
  status: EventEditor["status"];
  published_at: string | null;
}): EventEditStatusValue {
  return resolveArticleEditStatus(event);
}
